import React, { useState, useCallback } from "react";
import { Target, Dices, Users, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";

type GuildType = "planner" | "explorer" | "connector" | "creator";

interface GuildOption {
  id: GuildType;
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  descKey: string;
}

const GUILD_OPTIONS: GuildOption[] = [
  { id: "planner", icon: Target, labelKey: "profileCompletion.guildPlanner", descKey: "profileCompletion.guildPlannerDesc" },
  { id: "explorer", icon: Dices, labelKey: "profileCompletion.guildExplorer", descKey: "profileCompletion.guildExplorerDesc" },
  { id: "connector", icon: Users, labelKey: "profileCompletion.guildConnector", descKey: "profileCompletion.guildConnectorDesc" },
  { id: "creator", icon: Lightbulb, labelKey: "profileCompletion.guildCreator", descKey: "profileCompletion.guildCreatorDesc" },
];

interface GuildStepProps {
  onSelectGuild: (guild: GuildType) => void;
  onSkip: () => void;
  isPending: boolean;
}

export const GuildStep = React.memo(function GuildStep({
  onSelectGuild,
  onSkip,
  isPending,
}: GuildStepProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<GuildType | null>(null);

  const handleConfirm = useCallback(() => {
    if (selected) {
      onSelectGuild(selected);
    }
  }, [selected, onSelectGuild]);

  const selectedOption = selected ? GUILD_OPTIONS.find((g) => g.id === selected) : null;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          {t("profileCompletion.guildQuestion", "How do you fund your adventures?")}
        </DialogTitle>
        <DialogDescription>
          {t("profileCompletion.guildDesc", "Your guild shapes your trip planning style")}
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-3">
        {GUILD_OPTIONS.map((guild) => {
          const IconComponent = guild.icon;
          const isSelected = selected === guild.id;
          return (
            <Card
              key={guild.id}
              className={`p-3 cursor-pointer overflow-visible transition-all duration-200 hover:shadow-md active:scale-95 ${
                isSelected
                  ? "border-primary ring-2 ring-primary/20 shadow-md"
                  : "hover:border-primary/50"
              }`}
              onClick={() => setSelected(guild.id)}
              data-testid={`card-guild-${guild.id}`}
            >
              <div className="flex flex-col items-center text-center gap-2">
                <IconComponent className={`h-6 w-6 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                <p className="text-sm font-medium leading-tight">
                  {t(guild.labelKey)}
                </p>
                <p className="text-xs text-muted-foreground leading-snug">
                  {t(guild.descKey)}
                </p>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Guild badge preview after selection */}
      {selectedOption && (
        <div className="flex justify-center pt-2">
          <Badge variant="secondary" className="gap-1.5 px-3 py-1">
            {React.createElement(selectedOption.icon, { className: "h-3.5 w-3.5" })}
            {t("profileCompletion.guildSelected", { guild: t(selectedOption.labelKey), defaultValue: "Your guild: {{guild}}" })}
          </Badge>
        </div>
      )}

      <div className="flex flex-col gap-2 pt-2">
        <Button
          onClick={handleConfirm}
          disabled={!selected || isPending}
          className="w-full"
          data-testid="button-confirm-guild"
        >
          {isPending
            ? t("profileCompletion.saving", "Saving...")
            : t("profileCompletion.letsGo", "Let's go!")}
        </Button>
        <Button
          variant="ghost"
          onClick={onSkip}
          disabled={isPending}
          className="w-full"
          data-testid="button-skip-guild"
        >
          {t("profileCompletion.skipGuild", "Skip for now")}
        </Button>
      </div>
    </>
  );
});
